import type { CSSProperties, HTMLAttributes } from 'react'

import type { BlueprintModifier } from '@/grove-blueprints/generated-types'

import type { BlueprintBindings } from '../../blueprint-bindings'
import { resolveAccessibilityElementModifier } from './resolve-accessibility-element-modifier'
import { resolveAccessibilityHeadingModifier } from './resolve-accessibility-heading-modifier'
import { resolveAccessibilityHiddenModifier } from './resolve-accessibility-hidden-modifier'
import { resolveAccessibilityIdentifierModifier } from './resolve-accessibility-identifier-modifier'
import { resolveAccessibilityLabelModifier } from './resolve-accessibility-label-modifier'
import { resolveAccessibilityValueModifier } from './resolve-accessibility-value-modifier'
import { resolveBackgroundColorModifier } from './resolve-background-color-modifier'
import { resolveFontModifier } from './resolve-font-modifier'
import { resolveForegroundColorModifier } from './resolve-foreground-color-modifier'
import { resolveFrameModifier } from './resolve-frame-modifier'
import { resolveOpacityModifier } from './resolve-opacity-modifier'
import { resolvePaddingModifier } from './resolve-padding-modifier'

export type ResolvedModifiers = {
  readonly className: string
  readonly style: CSSProperties
  readonly attributes: Partial<HTMLAttributes<HTMLElement>> &
    Partial<Record<`data-${string}`, string>>
}

export function resolveModifiers(
  modifiers: ReadonlyArray<BlueprintModifier> | undefined,
  bindingValues: BlueprintBindings['bindingValues'],
  localizedString: (key: string) => string,
): ResolvedModifiers {
  const classes: Array<string> = []
  const style: CSSProperties = {}
  const attributes: ResolvedModifiers['attributes'] = {}

  if (!modifiers) {
    return {
      className: '',
      style,
      attributes,
    }
  }

  for (const modifier of modifiers) {
    switch (modifier.type) {
      case 'padding': {
        Object.assign(style, resolvePaddingModifier(modifier))
        break
      }
      case 'frame': {
        Object.assign(style, resolveFrameModifier(modifier))
        break
      }
      case 'font': {
        classes.push(resolveFontModifier(modifier))
        break
      }
      case 'foregroundColor': {
        classes.push(resolveForegroundColorModifier(modifier))
        break
      }
      case 'backgroundColor': {
        classes.push(resolveBackgroundColorModifier(modifier))
        break
      }
      case 'opacity': {
        Object.assign(style, resolveOpacityModifier(modifier))
        break
      }
      case 'visible': {
        // Handled by resolveVisibilityModifier in RenderGroveBlueprintNode.
        break
      }
      case 'accessibilityHidden': {
        Object.assign(attributes, resolveAccessibilityHiddenModifier(modifier))
        break
      }
      case 'accessibilityIdentifier': {
        Object.assign(
          attributes,
          resolveAccessibilityIdentifierModifier(modifier),
        )
        break
      }
      case 'accessibilityLabel': {
        Object.assign(
          attributes,
          resolveAccessibilityLabelModifier(
            modifier,
            bindingValues,
            localizedString,
          ),
        )
        break
      }
      case 'accessibilityValue': {
        Object.assign(
          attributes,
          resolveAccessibilityValueModifier(
            modifier,
            bindingValues,
            localizedString,
          ),
        )
        break
      }
      case 'accessibilityHeading': {
        Object.assign(attributes, resolveAccessibilityHeadingModifier(modifier))
        break
      }
      case 'accessibilityElement': {
        Object.assign(attributes, resolveAccessibilityElementModifier(modifier))
        break
      }
      default: {
        const unknownModifier: never = modifier
        if (process.env.SDUI_BLUEPRINT_STRICT_MODE === 'true') {
          throw new Error(
            `Unsupported Blueprint modifier: ${JSON.stringify(unknownModifier)}`,
          )
        }
        break
      }
    }
  }

  return {
    className: classes.join(' '),
    style,
    attributes,
  }
}
